import React, { FC, useEffect, useRef, useState } from 'react';
import { styled } from 'linaria/lib/react';

interface SlotReelNumberProps {
  value: number;
  duration?: number;
  delay?: number; // delay in ms before starting animation
}

const DIGIT_HEIGHT_EM = 1.2;
const EM_TO_PX = 16; // 1em = 16px (approximate)
const DIGIT_HEIGHT_PX = Math.round(DIGIT_HEIGHT_EM * EM_TO_PX); // round to avoid subpixel
const SPIN_COUNT = 2; // Number of full spins before landing on target

const StyledSlotReelContainer = styled.span`
  display: inline-flex;
  overflow: hidden;
  position: relative;
  height: ${DIGIT_HEIGHT_EM}em;
  min-height: ${DIGIT_HEIGHT_PX}px;
  max-height: ${DIGIT_HEIGHT_PX}px;
  line-height: ${DIGIT_HEIGHT_EM}em;
  box-sizing: border-box;
`;

const StyledDigitReel = styled.span`
  display: inline-block;
  height: ${DIGIT_HEIGHT_EM}em;
  min-height: ${DIGIT_HEIGHT_PX}px;
  max-height: ${DIGIT_HEIGHT_PX}px;
  overflow: hidden;
  position: relative;
  box-sizing: border-box;
`;

const StyledSlotReelStrip = styled.div<{ translateY: number; isAnimating: boolean; duration: number }>`
  display: flex;
  flex-direction: column;
  transform: translateY(${({ translateY }) => translateY}px);
  transition: ${({ isAnimating, duration }) =>
    isAnimating ? `transform ${duration}ms cubic-bezier(0.8, 0, 0.2, 1)` : 'none'};
  box-sizing: border-box;
`;

const StyledSlotReelDigit = styled.span`
  display: block;
  height: ${DIGIT_HEIGHT_EM}em;
  min-height: ${DIGIT_HEIGHT_PX}px;
  max-height: ${DIGIT_HEIGHT_PX}px;
  line-height: ${DIGIT_HEIGHT_EM}em;
  text-align: center;
  width: 100%;
  box-sizing: border-box;
`;

const getDigits = (num: number, minLength = 1) => {
  const digits = num.toString().split('').map(Number);
  while (digits.length < minLength) digits.unshift(0);
  return digits;
};

/**
 * SlotReelNumber component that animates a number by smoothly transitioning
 * through a strip of digits, creating a realistic slot machine reel effect.
 * The animation always lands on the target digit as part of the spin.
 *
 * @param value - The final number to display
 * @param duration - Animation duration in milliseconds (default: 750ms)
 * @param delay - Delay in milliseconds before starting the animation (default: 0ms)
 */
const SlotReelNumber: FC<SlotReelNumberProps> = ({ value, duration = 800, delay = 0 }) => {
  const targetDigit = getDigits(value, 1)[0];
  const [startDigit, setStartDigit] = useState(() => Math.floor(Math.random() * 10));
  const [isAnimating, setIsAnimating] = useState(false);
  const [translateY, setTranslateY] = useState(0);
  const prevValueRef = useRef<number | null>(null);
  const hasAnimatedForValue = useRef<number | null>(null);
  // Build the digit strip for this animation
  const buildStrip = (start: number, end: number, spins: number) => {
    const strip: number[] = [];
    let current = start;
    for (let spin = 0; spin < spins * 10; spin += 1) {
      strip.push(current);
      current = (current + 1) % 10;
    }
    // Now, step from current to end (inclusive)
    while (current !== end) {
      strip.push(current);
      current = (current + 1) % 10;
    }
    strip.push(end); // ensure the last digit is the target
    return strip;
  };
  const [digitStrip, setDigitStrip] = useState<number[]>(() => buildStrip(startDigit, targetDigit, SPIN_COUNT));

  useEffect(() => {
    // Only animate if value actually changes and hasn't animated for this value yet
    if (hasAnimatedForValue.current === value) return undefined;
    hasAnimatedForValue.current = value;
    // Pick a new random start digit for each animation
    const newStart = Math.floor(Math.random() * 10);
    setStartDigit(newStart);
    const newStrip = buildStrip(newStart, targetDigit, SPIN_COUNT);
    setDigitStrip(newStrip);
    setIsAnimating(false);
    setTranslateY(0);
    // Wait for delay before starting animation
    const delayTimeout = setTimeout(() => {
      setIsAnimating(true);
      // Animate to the last digit (target) in the strip
      const endOffset = -((newStrip.length - 1) * DIGIT_HEIGHT_PX);
      setTranslateY(endOffset);
      prevValueRef.current = value;
      // After animation, just stop animating but keep the strip and translateY
      const resetTimer = setTimeout(() => {
        setIsAnimating(false);
        // Do NOT replace the strip with a single digit; keep the strip and translateY
      }, duration + 50);
      return () => clearTimeout(resetTimer);
    }, delay);
    return () => {
      clearTimeout(delayTimeout);
    };
  }, [value, duration, delay, targetDigit]);

  return (
    <StyledSlotReelContainer>
      <StyledDigitReel>
        <StyledSlotReelStrip translateY={translateY} isAnimating={isAnimating} duration={duration}>
          {digitStrip.map((digit, idx) => (
            // eslint-disable-next-line react/no-array-index-key
            <StyledSlotReelDigit key={`${startDigit}-${digit}-${idx}`}>{digit}</StyledSlotReelDigit>
          ))}
        </StyledSlotReelStrip>
      </StyledDigitReel>
    </StyledSlotReelContainer>
  );
};

export default SlotReelNumber;
